<script>
  /**
   * Form component for SocialsTrack
   * A flexible, reusable form component with built-in validation and styling
   */

  // Props definition
  let {
    // Form configuration
    title = "",
    description = "",
    submitText = "Submit",
    cancelText = "Cancel",
    showCancel = false,
    processingText = "Processing...",

    // Form state
    loading = false,
    error = "",
    success = "",
    serverErrors = {},

    // Form fields configuration
    fields = [],

    // Form data and validation
    initialData = {},

    // Events
    onSubmit = () => {},
    onCancel = () => {},

    // Styling
    variant = "primary", // primary, secondary, minimal
    size = "md", // sm, md, lg

    // Additional props
    ...otherProps
  } = $props();

  // Internal state
  let formData = $state(structuredClone(initialData));
  let submitted = $state(false);

  // Compute button styling based on variant and size
  let buttonClass = $derived(getButtonClass());
  let inputClass = $derived(getInputClass());

  let generalError = $derived.by(() => {
    if (serverErrors && serverErrors.general) {
      return serverErrors.general;
    } else {
      return null;
    }
  });

  let formErrors = $derived.by(() => {
    let errors = {};

    if (serverErrors && serverErrors.errors) {
      Object.entries(serverErrors.errors).forEach(([key, errorMessages]) => {
        if (Array.isArray(errorMessages) && errorMessages.length > 0) {
          errors[key] = errorMessages[0];
        }
      });
    }

    return errors;
  });

  let touched = $derived.by(() => {
    let data = {};

    if (serverErrors && serverErrors.errors) {
      Object.entries(serverErrors.errors).forEach(([key, errorMessages]) => {
        if (Array.isArray(errorMessages) && errorMessages.length > 0) {
          data[key] = true;
        }
      });
    }

    return data;
  });

  function getButtonClass() {
    const baseClass =
      "relative w-full flex justify-center border font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition duration-200 ease-in-out";

    const variantClasses = {
      primary:
        "text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:ring-indigo-500 border-transparent shadow-md",
      secondary:
        "text-indigo-700 bg-gradient-to-r from-indigo-100 to-blue-100 hover:from-indigo-200 hover:to-blue-200 focus:ring-indigo-500 border-transparent",
      minimal:
        "text-gray-700 bg-white hover:bg-gray-50 focus:ring-indigo-500 border-gray-300 shadow-sm",
    };

    const sizeClasses = {
      sm: "py-1.5 px-3 text-xs",
      md: "py-2 px-4 text-sm",
      lg: "py-3 px-6 text-base",
    };

    return `${baseClass} ${variantClasses[variant] || variantClasses.primary} ${sizeClasses[size] || sizeClasses.md}`;
  }

  function getInputClass(hasError = false) {
    const baseClass =
      "appearance-none relative block w-full border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 transition duration-200 sm:text-sm bg-white shadow-sm";

    const errorClass = hasError
      ? "border-red-300 focus:ring-red-500 focus:border-red-500 ring-red-500"
      : "border-gray-300 focus:ring-indigo-500 focus:border-indigo-500";

    const sizeClasses = {
      sm: "py-1.5 px-3 text-xs",
      md: "py-2.5 px-4 text-sm",
      lg: "py-3.5 px-5 text-base",
    };

    return `${baseClass} ${errorClass} ${sizeClasses[size] || sizeClasses.md}`;
  }

  // Reset form to initial state
  function resetForm() {
    formData = structuredClone(initialData);
    formErrors = {};
    touched = {};
    submitted = false;
    generalError = "";
  }

  // Handle form submission
  function handleSubmit(event) {
    event.preventDefault();
    submitted = true;

    // Validate all fields
    const errors = validateForm();

    if (Object.keys(errors).length === 0) {
      // Form is valid, call the onSubmit handler
      onSubmit(formData);
    } else {
      // Form has errors
      formErrors = errors;
    }
  }

  // Validate the entire form
  function validateForm() {
    const errors = {};

    fields.forEach((field) => {
      if (field.required && !formData[field.name]) {
        errors[field.name] = `${field.label || field.name} is required`;
      }

      if (field.type === "email" && formData[field.name]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData[field.name])) {
          errors[field.name] = "Please enter a valid email address";
        }
      }

      if (
        field.minLength &&
        formData[field.name] &&
        formData[field.name].length < field.minLength
      ) {
        errors[field.name] =
          `${field.label || field.name} must be at least ${field.minLength} characters`;
      }

      if (field.validate && formData[field.name]) {
        const customError = field.validate(formData[field.name], formData);
        if (customError) {
          errors[field.name] = customError;
        }
      }
    });

    return errors;
  }

  // Handle input change
  function handleChange(event, fieldName) {
    const value = event.target.value;
    formData[fieldName] = value;

    // Mark field as touched
    touched[fieldName] = true;

    // Validate field if already submitted
    if (submitted) {
      const field = fields.find((f) => f.name === fieldName);

      if (field.required && !value) {
        formErrors[fieldName] = `${field.label || field.name} is required`;
      } else if (field.type === "email") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          formErrors[fieldName] = "Please enter a valid email address";
        } else {
          delete formErrors[fieldName];
        }
      } else if (field.minLength && value.length < field.minLength) {
        formErrors[fieldName] =
          `${field.label || field.name} must be at least ${field.minLength} characters`;
      } else if (field.validate) {
        const customError = field.validate(value, formData);
        if (customError) {
          formErrors[fieldName] = customError;
        } else {
          delete formErrors[fieldName];
        }
      } else {
        delete formErrors[fieldName];
      }
    }
  }

  // Handle blur event
  function handleBlur(fieldName) {
    touched[fieldName] = true;

    // Validate field
    const field = fields.find((f) => f.name === fieldName);

    if (field.required && !formData[fieldName]) {
      formErrors[fieldName] = `${field.label || field.name} is required`;
    } else if (field.type === "email" && formData[fieldName]) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData[fieldName])) {
        formErrors[fieldName] = "Please enter a valid email address";
      } else {
        delete formErrors[fieldName];
      }
    } else if (
      field.minLength &&
      formData[fieldName] &&
      formData[fieldName].length < field.minLength
    ) {
      formErrors[fieldName] =
        `${field.label || field.name} must be at least ${field.minLength} characters`;
    } else if (field.validate && formData[fieldName]) {
      const customError = field.validate(formData[fieldName], formData);
      if (customError) {
        formErrors[fieldName] = customError;
      } else {
        delete formErrors[fieldName];
      }
    } else {
      delete formErrors[fieldName];
    }
  }
</script>

<div class="w-full max-w-lg mx-auto" {...otherProps}>
  {#if title}
    <h2 class="text-2xl font-bold text-gray-800 mb-3 tracking-tight">
      {title}
    </h2>
  {/if}

  {#if description}
    <p class="text-gray-600 mb-6 text-sm leading-relaxed">{description}</p>
  {/if}

  {#if error || generalError}
    <div
      class="my-8 mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm shadow-sm animate-fade-in"
    >
      {error || generalError}
    </div>
  {/if}

  {#if success}
    <div
      class="my-8 mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm shadow-sm animate-fade-in"
    >
      {success}
    </div>
  {/if}

  <form onsubmit={handleSubmit} class="space-y-6">
    {#each fields as field}
      <div class="space-y-2">
        {#if field.type !== "checkbox"}
          <label
            for={field.name}
            class="block text-sm font-medium text-gray-700"
          >
            {field.label}
            {#if field.required}
              <span class="text-red-500 ml-1">*</span>
            {/if}
          </label>
        {/if}

        {#if field.type === "text" || field.type === "email" || field.type === "password" || field.type === "number"}
          <input
            id={field.name}
            name={field.name}
            type={field.type}
            placeholder={field.placeholder || ""}
            autocomplete={field.autocomplete || ""}
            class={getInputClass(formErrors[field.name] && touched[field.name])}
            bind:value={formData[field.name]}
            required={field.required || false}
            oninput={(e) => handleChange(e, field.name)}
            onblur={() => handleBlur(field.name)}
            disabled={field.disabled || loading}
            class:hover:ring-2={!(field.disabled || loading)}
            class:hover:ring-indigo-300={!(field.disabled || loading) &&
              !(formErrors[field.name] && touched[field.name])}
          />
        {:else if field.type === "textarea"}
          <textarea
            id={field.name}
            name={field.name}
            placeholder={field.placeholder || ""}
            class={`${getInputClass(formErrors[field.name] && touched[field.name])} min-h-[120px] resize-y`}
            bind:value={formData[field.name]}
            required={field.required || false}
            oninput={(e) => handleChange(e, field.name)}
            onblur={() => handleBlur(field.name)}
            disabled={field.disabled || loading}
            class:hover:ring-2={!(field.disabled || loading)}
            class:hover:ring-indigo-300={!(field.disabled || loading) &&
              !(formErrors[field.name] && touched[field.name])}
          ></textarea>
        {:else if field.type === "select"}
          <div class="relative">
            <select
              id={field.name}
              name={field.name}
              class={`${getInputClass(formErrors[field.name] && touched[field.name])} appearance-none`}
              bind:value={formData[field.name]}
              oninput={(e) => handleChange(e, field.name)}
              onblur={() => handleBlur(field.name)}
              disabled={field.disabled || loading}
              class:hover:ring-2={!(field.disabled || loading)}
              class:hover:ring-indigo-300={!(field.disabled || loading) &&
                !(formErrors[field.name] && touched[field.name])}
            >
              {#each field.options as option}
                <option
                  value={option.value}
                  selected={formData[field.name] === option.value}
                >
                  {option.label}
                </option>
              {/each}
            </select>
          </div>
        {:else if field.type === "checkbox"}
          <div class="flex items-center">
            <input
              id={field.name}
              name={field.name}
              type="checkbox"
              class="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded-md shadow-sm"
              bind:checked={formData[field.name]}
              oninput={(e) => {
                formData[field.name] = e.target.checked;
                if (submitted) {
                  if (field.required && !e.target.checked) {
                    formErrors[field.name] =
                      `${field.label || field.name} is required`;
                  } else {
                    delete formErrors[field.name];
                  }
                }
              }}
              onblur={() => handleBlur(field.name)}
              disabled={field.disabled || loading}
            />
            <label
              for={field.name}
              class="ml-3 block text-sm font-medium text-gray-700"
            >
              {field.checkboxLabel || field.label}
            </label>
          </div>
        {:else}
          <input
            id={field.name}
            name={field.name}
            type="text"
            placeholder={field.placeholder || ""}
            class={getInputClass(formErrors[field.name] && touched[field.name])}
            bind:value={formData[field.name]}
            required={field.required || false}
            oninput={(e) => handleChange(e, field.name)}
            onblur={() => handleBlur(field.name)}
            disabled={field.disabled || loading}
            class:hover:ring-2={!(field.disabled || loading)}
            class:hover:ring-indigo-300={!(field.disabled || loading) &&
              !(formErrors[field.name] && touched[field.name])}
          />
        {/if}

        {#if field.helpText}
          <p class="mt-1 text-xs text-gray-500 italic">{field.helpText}</p>
        {/if}

        {#if formErrors[field.name] && touched[field.name]}
          <p class="mt-1 text-xs text-red-600 font-medium animate-fade-in">
            {formErrors[field.name]}
          </p>
        {/if}
      </div>
    {/each}

    <div class={showCancel ? "flex space-x-4 pt-4" : "pt-4"}>
      <button
        type="submit"
        class={`${buttonClass} shadow-md hover:shadow-lg transition-all duration-200`}
        disabled={loading}
      >
        {#if loading}
          <svg
            class="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          {processingText}
        {:else}
          {submitText}
        {/if}
      </button>

      {#if showCancel}
        <button
          type="button"
          class="relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-200 ease-in-out shadow-sm hover:shadow-md"
          onclick={onCancel}
          disabled={loading}
        >
          {cancelText}
        </button>
      {/if}
    </div>
  </form>
</div>
